// ** Modules
import clsx from 'clsx';
import React from 'react';
import { useRouter } from 'next/router';

// ** UI
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import Box, { type BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import Translations from '@/components/common/Translations';
import VerticalNavItems from './VerticalNavItems';
import { Icon } from '@/components/common/icons';

// ** Configs
import themeConfig from '@/theme/themeConfig';

// ** Utils
import { hexToRGBA } from '@/utils/ui/styleUtils';
import { hasActiveChild, removeChildren, buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { NavGroup, LayoutProps } from '@/types/layoutProps';

type Props = {
	item: NavGroup;
	navHover: boolean;
	parent?: NavGroup;
	navVisible?: boolean;
	groupActive: string[];
	nestedlevel?: number;
	collapsedNavWidth: number;
	currentActiveGroup: string[];
	navigationBorderWidth: number;
	settings: LayoutProps['settings'];
	isSubToSub?: NavGroup | undefined;
	saveSettings: LayoutProps['saveSettings'];
	setGroupActive: (values: string[]) => void;
	setCurrentActiveGroup: (items: string[]) => void;
};

const MenuItemTextWrapper = styled(Box)<BoxProps>({
	width: '100%',
	display: 'flex',
	alignItems: 'flex-start',
	transition: 'opacity .25s ease-in-out',
	fontWeight: 'bold',
	...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
});

const VerticalNavGroup = (props: Props) => {
	// Props
	const {
		item,
		parent,
		settings,
		navHover,
		navVisible,
		isSubToSub,
		groupActive,
		nestedlevel,
		setGroupActive,
		collapsedNavWidth,
		currentActiveGroup,
		setCurrentActiveGroup,
		navigationBorderWidth
	} = props;

	// ** Hooks & Vars
	const router = useRouter();
	const currentUrl = router.asPath;
	const { navcollapsed, verticalNavToggleType } = settings;

	// ** Accordion menu group open toggle
	const toggleActiveGroup = (item: NavGroup, parent: NavGroup | undefined) => {
		let openGroup = groupActive;

		// ** If Group is already open and clicked, close the group
		if (openGroup.includes(item.title)) {
			openGroup.splice(openGroup.indexOf(item.title), 1);

			// If clicked Group has open group children, Also remove those children to close those groups
			if (item.children) {
				removeChildren(item.children, openGroup, currentActiveGroup);
			}
		} else if (parent) {
			// ** If Group clicked is the child of an open group, first remove all the open groups under that parent
			if (parent.children) {
				removeChildren(parent.children, openGroup, currentActiveGroup);
			}

			// ** After removing all the open groups under that parent, add the clicked group to open group array
			if (!openGroup.includes(item.title)) {
				openGroup.push(item.title);
			}
		} else {
			// ** If clicked on another group that is not active or open, create openGroup array from scratch

			// ** Empty Open Group array
			openGroup = [];

			// ** push Current Active Group To Open Group array
			if (currentActiveGroup.every((elem) => groupActive.includes(elem))) {
				openGroup.push(...currentActiveGroup);
			}

			// ** Push current clicked group item to Open Group array
			if (!openGroup.includes(item.title)) {
				openGroup.push(item.title);
			}
		}
		setGroupActive([...openGroup]);
	};

	// ** Menu Group Click
	const handleGroupClick = () => {
		const openGroup = groupActive;
		if (verticalNavToggleType === 'collapse') {
			if (openGroup.includes(item.title)) {
				openGroup.splice(openGroup.indexOf(item.title), 1);
			} else {
				openGroup.push(item.title);
			}
			setGroupActive([...openGroup]);
		} else {
			toggleActiveGroup(item, parent);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: More dependencies will make he navbar bugged, TODO: needs rework
	React.useEffect(() => {
		if (hasActiveChild(item, currentUrl)) {
			if (!groupActive.includes(item.title)) groupActive.push(item.title);
		} else {
			const index = groupActive.indexOf(item.title);
			if (index > -1) groupActive.splice(index, 1);
		}
		setGroupActive([...groupActive]);
		setCurrentActiveGroup([...groupActive]);

		// Empty Active Group When Menu is collapsed and not hovered, to fix issue route change
		if (navcollapsed && !navHover) {
			setGroupActive([]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.asPath]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: More dependencies will make he navbar bugged, TODO: needs rework
	React.useEffect(() => {
		if (navcollapsed && !navHover) {
			setGroupActive([]);
		}

		if ((navcollapsed && navHover) || (groupActive.length === 0 && !navcollapsed)) {
			setGroupActive([...currentActiveGroup]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navcollapsed, navHover]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: More dependencies will make he navbar bugged, TODO: needs rework
	React.useEffect(() => {
		if (groupActive.length === 0 && !navcollapsed) {
			setGroupActive([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navHover]);

	const icon = parent && !item?.icon?.icon ? themeConfig.navSubItemIcon : item?.icon?.icon;
	const resolvedPath = item.url || (item.pageLink ? buildPathFromRelationLink(item.pageLink) : undefined);

	const menuGroupCollapsedStyles = navcollapsed && !navHover ? { opacity: 0 } : { opacity: 1 };

	const getIconColor = (isSelected: boolean) => {
		if (nestedlevel === 1) {
			return isSelected ? '#FFFFFF' : '#DF7A00';
		}
		return isSubToSub ? '#FFFFFF' : 'text.primary';
	};

	return (
		// <CanViewNavGroup navGroup={item}>
		// 	<Fragment>
		// 	</Fragment>
		// </CanViewNavGroup>
		<ListItem
			disablePadding
			onClick={handleGroupClick}
			sx={{
				mt: nestedlevel ? 1.5 : 5,
				px: '0 !important',
				flexDirection: 'column'
			}}
		>
			<ListItemButton
				className={clsx({
					'Mui-selected':
						groupActive.includes(item.title) || currentActiveGroup.includes(item.title) || currentUrl === resolvedPath
				})}
				sx={(theme) => ({
					py: nestedlevel ? 1 : 2.25,
					width: '100%',
					color: isSubToSub ? theme.palette.primary.main : theme.palette.text.primary,
					// borderTopRightRadius: 100,
					// borderBottomRightRadius: 100,
					transition: 'padding-left .25s ease-in-out, background .25s ease-in-out',
					pl: navcollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 24) / 8 : 5.5,
					pr: navcollapsed && !navHover ? ((collapsedNavWidth - navigationBorderWidth - 24) / 2 - 5) / 4 : 3.5,
					'&.Mui-selected': {
						backgroundColor:
							nestedlevel === 1
								? hexToRGBA(theme.palette.primary.main, 0.6)
								: nestedlevel === 2
									? hexToRGBA(theme.palette.primary.main, 0.5)
									: theme.palette.primary.main,
						color: theme.palette.common.white,
						boxShadow: theme.shadows[3],
						'&:hover': {
							backgroundColor: theme.palette.primary.lighter
						},
						'& .MuiListItemIcon-root, .MuiBox-root > svg': {
							color: theme.palette.common.white
						}
					},
					'&.Mui-selected.Mui-focusVisible': {
						backgroundColor: 'action.focus',
						'&:hover': {
							backgroundColor: 'action.focus'
						}
					},
					'&:hover': {
						backgroundColor: nestedlevel === 2 ? theme.palette.primary.lighter : 'transparent'
					}
				})}
			>
				{isSubToSub ? null : (
					<ListItemIcon
						sx={{
							minWidth: 'unset',
							color: 'primary.main',
							transition: 'margin .25s ease-in-out',
							...(parent && navcollapsed && !navHover ? {} : { mr: 2.5 }),
							...(navcollapsed && !navHover ? { mr: 0 } : {}), // this condition should come after (parent && navcollapsed && !navHover) condition for proper styling
							...(parent && item.children ? { ml: 1.25, mr: 3.75 } : {})
						}}
					>
						<Icon name={icon as string} {...(parent && { fontSize: '0.875rem' })} />
					</ListItemIcon>
				)}

				<MenuItemTextWrapper
					sx={{
						color: 'inherit',
						...menuGroupCollapsedStyles
					}}
				>
					{resolvedPath ? (
						<Link
							href={resolvedPath}
							color="inherit"
							target={resolvedPath.startsWith('http://') || resolvedPath.startsWith('https://') ? '_blank' : undefined}
							rel={
								resolvedPath.startsWith('http://') || resolvedPath.startsWith('https://')
									? 'noopener noreferrer'
									: undefined
							}
							style={{ textDecoration: 'none' }}
						>
							<Typography
								{...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navcollapsed && !navHover)) && {
									noWrap: true
								})}
							>
								<Translations text={item.title} />
							</Typography>
						</Link>
					) : (
						<Typography
							{...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navcollapsed && !navHover)) && {
								noWrap: true
							})}
						>
							<Translations text={item.title} />
						</Typography>
					)}

					<Box
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
							'& svg': {
								color: 'text.primary'
								// transition: 'transform .25s ease-in-out'
								// ...(groupActive.includes(item.title) && {
								// 	transform: 'rotate(90deg)'
								// })
							}
						}}
					>
						{item.badgeContent ? (
							<Chip
								label={item.badgeContent}
								color={item.badgeColor || 'primary'}
								sx={{
									mr: 1.5,
									height: 20,
									fontWeight: 500,
									'& .MuiChip-label': {
										px: 1.5,
										textTransform: 'capitalize'
									}
								}}
							/>
						) : null}

						<Icon
							fontSize="medium"
							name={groupActive.includes(item.title) ? 'mdi-minus' : 'mdi-plus'}
							style={{ marginLeft: 5 }}
							color={getIconColor(groupActive.includes(item.title) || currentActiveGroup.includes(item.title))}
						/>
					</Box>
				</MenuItemTextWrapper>
			</ListItemButton>
			<Collapse
				component="ul"
				onClick={(e) => e.stopPropagation()}
				in={groupActive.includes(item.title)}
				sx={{
					mt: !nestedlevel ? 1 : 0,
					width: '100%',
					...menuGroupCollapsedStyles,
					...(isSubToSub && { p: 0 }),
					transition: 'all 0.25s ease-in-out'
				}}
			>
				<VerticalNavItems
					{...props}
					parent={item}
					navVisible={navVisible}
					verticalNavItems={item.children}
					isSubToSub={parent && item.children ? item : undefined}
				/>
			</Collapse>
			{nestedlevel === 1 && groupActive.includes(item.title) && (
				<Divider
					sx={{
						mt: 3.5,
						mb: 1.5,
						width: '136%',
						padding: 0.5,
						position: 'relative',
						left: -40,
						background: (theme) => theme.palette.primary.main
					}}
				/>
			)}
		</ListItem>
	);
};

export default VerticalNavGroup;
